import * as React from 'react';
import { useContext, useState } from 'react';
import SpotReviewContext from '../../context/spot/spotReviewContext';
import AlertContext from '../../context/alert/alertContext';
import IconButton from '@mui/joy/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Avatar from '@mui/joy/Avatar';
import Box from '@mui/joy/Box';
import Card from '@mui/joy/Card';
import Typography from '@mui/joy/Typography';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TimerIcon from '@mui/icons-material/Timer';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import { useHistory } from 'react-router-dom';

const SpotReviewItemV2 = ({ spotReview, isDraft, draftIndex, showDelete, fromPath }) => {
    const [photoIdx, setPhotoIdx] = useState(0);
    const history = useHistory();
    const { deleteSpotReview, removeDraftSpotReview } = useContext(SpotReviewContext);
    const { setAlert } = useContext(AlertContext);
    const handleDelete = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (isDraft) {
            removeDraftSpotReview(draftIndex);
            return;
        }
        if (!window.confirm('Delete this spot review?')) return;
        const result = await deleteSpotReview(spotReview.id);
        if (result && !result.ok) {
            setAlert(result.msg || 'Failed to delete spot review', 'error');
        }
    };
    const {
        title,
        content,
        owner_first_name,
        photos,
        owner_last_name,
        avatar,
        visit_start_time,
        visitStartTime,
        visit_duration,
        visitDuration,
        visit_from,
        visit_to,
        price,
    } = spotReview;

    const startTime =
        visit_start_time ||
        visitStartTime ||
        (visit_from ? new Date(visit_from * 1000).toISOString() : null);
    const duration =
        visit_duration ||
        visitDuration ||
        (visit_from && visit_to && visit_to > visit_from
            ? Math.round((visit_to - visit_from) / 60)
            : null);

    const formatStartTime = (t) => {
        if (!t) return null;
        const d = new Date(t);
        if (isNaN(d.getTime())) return null;
        return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    };

    const formatDuration = (mins) => {
        if (!mins) return null;
        const m = parseInt(mins);
        if (m >= 1440) return 'Full day';
        if (m >= 60) return m % 60 === 0 ? `${m / 60}h` : `${Math.floor(m / 60)}h ${m % 60}m`;
        return `${m}m`;
    };

    const handleCardClick = () => {
        if (!isDraft) {
            const effectiveFromPath = fromPath || '/spots';
            history.push(`/review/${spotReview.id}?from=${encodeURIComponent(effectiveFromPath)}`);
        }
    };

    const hasPhoto = photos && photos.length > 0 && photos[0] && photos[0].image_link;

    return (
        <Card
            orientation={hasPhoto ? "horizontal" : "vertical"}
            variant="outlined"
            onClick={handleCardClick}
            sx={{
                p: 0,
                minWidth: 0,
                overflow: 'hidden',
                position: 'relative',
                cursor: isDraft ? 'default' : 'pointer',
                transition: 'transform 200ms ease, box-shadow 200ms ease',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 'md',
                },
            }}
        >
            {/* Photo section — only when photos exist */}
            {hasPhoto && (
            <Box
                sx={{
                    position: 'relative',
                    width: { xs: 160, sm: 220 },
                    minWidth: { xs: 160, sm: 220 },
                    minHeight: { xs: 180, sm: 200 },
                    overflow: 'hidden',
                    flexShrink: 0,
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        width: '100%',
                        height: '100%',
                        transform: `translateX(-${photoIdx * 100}%)`,
                        transition: 'transform 400ms ease',
                    }}
                >
                    {photos.map((p, i) => (
                        <Box
                            key={i}
                            sx={{ flex: '0 0 100%', width: '100%', height: '100%' }}
                        >
                            {p && p.image_link && (
                                <img
                                    src={p.image_link}
                                    loading="lazy"
                                    alt=""
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        display: 'block',
                                    }}
                                />
                            )}
                        </Box>
                    ))}
                </Box>
                {photos && photos.length > 1 && (
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 8,
                            left: 0,
                            right: 0,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: 0.75,
                        }}
                    >
                        <IconButton
                            size="sm"
                            variant="plain"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setPhotoIdx((photoIdx - 1 + photos.length) % photos.length);
                            }}
                            sx={{
                                bgcolor: 'transparent',
                                color: 'white',
                                minHeight: 18,
                                minWidth: 18,
                                '--IconButton-size': '18px',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' },
                            }}
                        >
                            <ChevronLeftIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                            {photos.map((_, i) => (
                                <Box
                                    key={i}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setPhotoIdx(i);
                                    }}
                                    sx={{
                                        width: 6,
                                        height: 6,
                                        borderRadius: '50%',
                                        cursor: 'pointer',
                                        bgcolor: i === photoIdx ? 'white' : 'rgba(255,255,255,0.5)',
                                        transition: 'background-color 300ms ease',
                                    }}
                                />
                            ))}
                        </Box>
                        <IconButton
                            size="sm"
                            variant="plain"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setPhotoIdx((photoIdx + 1) % photos.length);
                            }}
                            sx={{
                                bgcolor: 'transparent',
                                color: 'white',
                                minHeight: 18,
                                minWidth: 18,
                                '--IconButton-size': '18px',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' },
                            }}
                        >
                            <ChevronRightIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Box>
                )}
            </Box>
            )}

            {/* Content section */}
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, p: 2, minWidth: 0, overflow: 'hidden' }}>
                {/* Author row */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    {avatar && (
                        <Avatar
                            src={avatar.image_link}
                            size="sm"
                            sx={{ '--Avatar-size': '1.5rem' }}
                        />
                    )}
                    {(spotReview.owner_name || owner_first_name) && (
                        <Typography level="body-xs" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                            {spotReview.owner_name || (owner_first_name + " " + owner_last_name)}
                        </Typography>
                    )}
                </Box>

                {/* Title */}
                <Typography level="title-md" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {title}
                </Typography>

                {/* Content */}
                <Typography
                    level="body-sm"
                    sx={{
                        color: 'text.secondary',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                    }}
                >
                    {content}
                </Typography>

                <Box sx={{ flexGrow: 1 }} />

                {/* Bottom row: metadata + delete */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1 }}>
                    {startTime && (
                        <Typography
                            level="body-xs"
                            startDecorator={<AccessTimeIcon sx={{ fontSize: 13 }} />}
                            sx={{ color: 'text.tertiary' }}
                        >
                            {formatStartTime(startTime)}
                        </Typography>
                    )}
                    {duration && (
                        <Typography
                            level="body-xs"
                            startDecorator={<TimerIcon sx={{ fontSize: 13 }} />}
                            sx={{ color: 'text.tertiary' }}
                        >
                            {formatDuration(duration)}
                        </Typography>
                    )}
                    {!!price && Number(price) !== 0 && (
                        <Typography
                            level="body-xs"
                            startDecorator={<PaidOutlinedIcon sx={{ fontSize: 13 }} />}
                            sx={{ color: 'text.tertiary' }}
                        >
                            ~${price}/person
                        </Typography>
                    )}
                </Box>
            </Box>
            {/* Delete — pinned to the card's top-right so it stays visible
                at narrow widths instead of being squeezed by the meta row. */}
            {(showDelete || isDraft) && (
                <IconButton
                    variant="plain"
                    color="danger"
                    size="sm"
                    onClick={handleDelete}
                    aria-label="delete spot review"
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        zIndex: 2,
                        '--IconButton-size': '26px',
                    }}
                >
                    <DeleteIcon sx={{ fontSize: 16 }} />
                </IconButton>
            )}
        </Card>
    );
}

export default SpotReviewItemV2;
